/**
 * useSpaceState - Room/Space state management for new wall volume system
 *
 * A Space (room) is defined by:
 * - A boundary polygon (inner face points from walls)
 * - References to wall segments that form its boundary
 * - Calculated area and perimeter from inner polygon
 * - Knowledge of which wall inner surfaces belong to it
 */

import { useState, useCallback, useMemo } from 'react';
import { Vector } from '../../geometry/base/Vector';

let spaceIdCounter = 0;
function generateSpaceId() {
  return `space_${Date.now()}_${++spaceIdCounter}`;
}

/**
 * Calculate polygon area using the shoelace formula (2D, XY plane)
 * @param {Array} points - Array of {x, y} points
 * @returns {number} Area in mm² (absolute value)
 */
function calcPolygonArea(points) {
  if (points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area) / 2;
}

/**
 * Calculate polygon perimeter
 * @param {Array} points - Array of {x, y} points
 * @returns {number} Perimeter in mm
 */
function calcPolygonPerimeter(points) {
  if (points.length < 2) return 0;
  let perimeter = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const dx = points[j].x - points[i].x;
    const dy = points[j].y - points[i].y;
    perimeter += Math.sqrt(dx * dx + dy * dy);
  }
  return perimeter;
}

/**
 * Calculate polygon centroid
 * @param {Array} points - Array of {x, y} points
 * @returns {{x, y}} Centroid
 */
function calcCentroid(points) {
  if (points.length === 0) return { x: 0, y: 0 };
  const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
  const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
  return { x: cx, y: cy };
}

/**
 * Create a space data object
 */
function createSpaceData(options = {}) {
  return {
    id: generateSpaceId(),
    name: options.name || 'Raum',
    // Boundary: either from wall segments or a drawn polygon
    boundarySegments: options.boundarySegments || [],
    // Direct polygon points (alternative to wall segments)
    polygonPointIds: options.polygonPointIds || [],
    // Cached inner polygon coordinates (derived from wall inner faces or polygon)
    innerPolygon: options.innerPolygon || [],
    // Which wall surfaces belong to this room
    wallSurfaces: options.wallSurfaces || [],
    // Calculated quantities
    area: 0,      // mm²
    perimeter: 0, // mm
    // Display
    colorIndex: options.colorIndex ?? 0,
    // Room properties
    usage: options.usage || '',         // e.g. 'Wohnzimmer', 'Flur'
    floorFinish: options.floorFinish || null,
    ceilingHeight: options.ceilingHeight || null,
    // Timestamps
    createdAt: Date.now(),
    modifiedAt: Date.now(),
  };
}

/**
 * Hook for managing space/room state in the new wall volume system
 * @param {Object} wallState - Reference to useWallState for wall lookups
 * @returns {Object} Space state and operations
 */
export function useSpaceState(wallState = null) {
  const [spaces, setSpaces] = useState([]);

  // ============= CREATE =============

  /**
   * Create a space from wall boundary segments.
   * Each segment references a wall and which side (inner) faces the room.
   * @param {Array} boundarySegments - [{ wallId, side: 'inner'|'outer' }]
   * @param {Object} options - { name, colorIndex, usage }
   * @returns {Object} Created space
   */
  const createSpaceFromWalls = useCallback((boundarySegments, options = {}) => {
    // Build inner polygon from wall inner face points
    const walls = wallState?.walls || [];
    const innerPolygon = [];
    const wallSurfaces = [];

    for (const seg of boundarySegments) {
      const wall = walls.find(w => w.id === seg.wallId);
      if (!wall) continue;

      const pts = wall.points;
      const side = seg.side || 'inner';

      // Get the two bottom points for this side
      let p1, p2;
      if (side === 'inner') {
        p1 = pts.innerBottomStart;
        p2 = pts.innerBottomEnd;
      } else {
        p1 = pts.outerBottomStart;
        p2 = pts.outerBottomEnd;
      }

      // Add start point (avoid duplicates with previous segment's end)
      if (innerPolygon.length === 0 ||
          Math.abs(innerPolygon[innerPolygon.length - 1].x - p1.x) > 1 ||
          Math.abs(innerPolygon[innerPolygon.length - 1].y - p1.y) > 1) {
        innerPolygon.push({ x: p1.x, y: p1.y });
      }
      innerPolygon.push({ x: p2.x, y: p2.y });

      // Calculate wall surface area for this segment
      const baseStart = Vector.fromObject(wall.baseStart);
      const baseEnd = Vector.fromObject(wall.baseEnd);
      const segLength = baseStart.distanceTo(baseEnd);
      wallSurfaces.push({
        wallId: wall.id,
        side,
        area: segLength * wall.height, // mm²
      });
    }

    const space = createSpaceData({
      ...options,
      boundarySegments,
      innerPolygon,
      wallSurfaces,
    });

    // Calculate quantities
    space.area = calcPolygonArea(innerPolygon);
    space.perimeter = calcPolygonPerimeter(innerPolygon);

    setSpaces(prev => [...prev, space]);
    return space;
  }, [wallState?.walls]);

  /**
   * Create a space from a drawn polygon (geometry curve).
   * The polygon defines the inner boundary; walls are auto-associated later.
   * @param {Array} points - Array of {x, y} points forming the polygon
   * @param {Object} options
   * @returns {Object} Created space
   */
  const createSpaceFromPolygon = useCallback((points, options = {}) => {
    const innerPolygon = points.map(p => ({ x: p.x, y: p.y }));

    const space = createSpaceData({
      ...options,
      innerPolygon,
    });

    space.area = calcPolygonArea(innerPolygon);
    space.perimeter = calcPolygonPerimeter(innerPolygon);

    // Auto-associate with wall inner surfaces
    const walls = wallState?.walls || [];
    const wallSurfaces = [];

    for (const wall of walls) {
      const pts = wall.points;
      const ibs = pts.innerBottomStart;
      const ibe = pts.innerBottomEnd;

      // Check if any edge of the polygon lies along this wall's inner face
      // Simple heuristic: check if wall midpoint is near polygon boundary
      const wallMid = { x: (ibs.x + ibe.x) / 2, y: (ibs.y + ibe.y) / 2 };

      // Check if wall midpoint is inside the polygon (ray casting)
      if (isPointInPolygon(wallMid, innerPolygon)) {
        const baseStart = Vector.fromObject(wall.baseStart);
        const baseEnd = Vector.fromObject(wall.baseEnd);
        const segLength = baseStart.distanceTo(baseEnd);
        wallSurfaces.push({
          wallId: wall.id,
          side: 'inner',
          area: segLength * wall.height,
        });
      }
    }

    space.wallSurfaces = wallSurfaces;

    setSpaces(prev => [...prev, space]);
    return space;
  }, [wallState?.walls]);

  // ============= UPDATE =============

  /**
   * Update space properties
   */
  const updateSpace = useCallback((spaceId, updates) => {
    setSpaces(prev => prev.map(s => {
      if (s.id !== spaceId) return s;
      const updated = { ...s, ...updates, modifiedAt: Date.now() };
      // Recalculate if polygon changed
      if (updates.innerPolygon) {
        updated.area = calcPolygonArea(updates.innerPolygon);
        updated.perimeter = calcPolygonPerimeter(updates.innerPolygon);
      }
      return updated;
    }));
  }, []);

  /**
   * Recalculate all spaces from current wall positions
   */
  const recalculateAllSpaces = useCallback(() => {
    const walls = wallState?.walls || [];

    setSpaces(prev => prev.map(space => {
      // Rebuild inner polygon from wall segments
      if (space.boundarySegments.length > 0) {
        const innerPolygon = [];
        const wallSurfaces = [];

        for (const seg of space.boundarySegments) {
          const wall = walls.find(w => w.id === seg.wallId);
          if (!wall) continue;

          const pts = wall.points;
          const side = seg.side || 'inner';
          let p1, p2;
          if (side === 'inner') {
            p1 = pts.innerBottomStart;
            p2 = pts.innerBottomEnd;
          } else {
            p1 = pts.outerBottomStart;
            p2 = pts.outerBottomEnd;
          }

          if (innerPolygon.length === 0 ||
              Math.abs(innerPolygon[innerPolygon.length - 1].x - p1.x) > 1 ||
              Math.abs(innerPolygon[innerPolygon.length - 1].y - p1.y) > 1) {
            innerPolygon.push({ x: p1.x, y: p1.y });
          }
          innerPolygon.push({ x: p2.x, y: p2.y });

          const baseStart = Vector.fromObject(wall.baseStart);
          const baseEnd = Vector.fromObject(wall.baseEnd);
          const segLength = baseStart.distanceTo(baseEnd);
          wallSurfaces.push({
            wallId: wall.id,
            side,
            area: segLength * wall.height,
          });
        }

        return {
          ...space,
          innerPolygon,
          wallSurfaces,
          area: calcPolygonArea(innerPolygon),
          perimeter: calcPolygonPerimeter(innerPolygon),
          modifiedAt: Date.now(),
        };
      }

      return space;
    }));
  }, [wallState?.walls]);

  // ============= DELETE =============

  const deleteSpace = useCallback((spaceId) => {
    setSpaces(prev => prev.filter(s => s.id !== spaceId));
  }, []);

  // ============= QUERIES =============

  const getSpace = useCallback((id) => {
    return spaces.find(s => s.id === id) || null;
  }, [spaces]);

  /**
   * Get all spaces that reference a given wall
   */
  const getSpacesForWall = useCallback((wallId) => {
    return spaces.filter(s =>
      s.wallSurfaces.some(ws => ws.wallId === wallId) ||
      s.boundarySegments.some(bs => bs.wallId === wallId)
    );
  }, [spaces]);

  // ============= COMPUTED =============

  const totalFloorArea = useMemo(() => {
    return spaces.reduce((sum, s) => sum + s.area, 0);
  }, [spaces]);

  // ============= SERIALIZATION =============

  const toJSON = useCallback(() => ({
    spaces: spaces.map(s => ({ ...s })),
  }), [spaces]);

  const fromJSON = useCallback((json) => {
    if (json.spaces) setSpaces(json.spaces);
  }, []);

  const clear = useCallback(() => {
    setSpaces([]);
  }, []);

  return {
    spaces,
    createSpaceFromWalls,
    createSpaceFromPolygon,
    updateSpace,
    deleteSpace,
    getSpace,
    getSpacesForWall,
    recalculateAllSpaces,
    totalFloorArea,
    toJSON,
    fromJSON,
    clear,
  };
}

/**
 * Ray casting point-in-polygon test (2D)
 */
function isPointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;

    const intersect = ((yi > point.y) !== (yj > point.y)) &&
      (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export default useSpaceState;
